import { FieldValidation } from '@rjsf/utils';
import Ajv from 'ajv';
import * as YAML from 'yaml';

import { ApiHolder } from '@backstage/core-plugin-api';
import { CatalogApi, catalogApiRef } from '@backstage/plugin-catalog-react';
import { FieldExtensionUiSchema } from '@backstage/plugin-scaffolder-react';

import { DaasApplicationInputUiOptions } from './DaasApplicationInput';
import { DaasApplicationSetInputUiOptions } from './DaasApplicationSetInput';
import { argoCDGeneratorSchema } from './schema/generatorsSchema';
import { imagesSchema } from './schema/imagesSchema';
import { argoCDTemplatePatchSchema } from './schema/templatePatchSchema';
import {
  ClusterConfiguration,
  DaasApplicationInputData,
  DaasApplicationSetInputData,
  GitWriteBackConfig,
  KubernetesMetadata,
  MetadataKeyValuePair,
  SourceConfiguration,
  SyncOption,
} from './types';
import { inferRepoType, resolveRepoType } from './utils';

/**
 * Frontstage copy of the DaaS source URL allowlist, expressed as regexes.
 *
 * Canonical list: charts/argocd-project/values.yaml `allowedRepoPatterns:` in
 * the deployment-as-a-service repo. The chart uses path-glob syntax
 * (`*` = one segment, `**` = recursive subpath); these regexes are equivalent
 * translations. Optional trailing slashes are accepted on exact-match entries.
 * Keep this list in sync — the platform team mirrors approved PRs from the
 * daas repo into this file.
 *
 * This list will be removed after the implementation of DaaS backend.
 */
const ALLOWED_REPO_PATTERNS: RegExp[] = [
  /^https:\/\/github\.com\/tomtom-internal\/[^/]+$/,
  /^https:\/\/github\.com\/tomtom-international\/[^/]+$/,
  /^https:\/\/tomtom-international\.github\.io\/helm-charts$/,
  /^https:\/\/artifactory\.tomtomgroup\.com\/.+$/,
  /^https:\/\/tomtom\.jfrog\.io\/.+$/,
  /^https:\/\/airflow\.apache\.org$/,
  /^https:\/\/grafana\.github\.io\/helm-charts$/,
  /^https:\/\/grafana-community\.github\.io\/helm-charts$/,
  /^https:\/\/helm\.elastic\.co$/,
  /^https:\/\/helm\.releases\.hashicorp\.com$/,
  /^https:\/\/prometheus-community\.github\.io\/helm-charts$/,
  /^https:\/\/repo\.blackduck\.com\/artifactory\/cloudnative$/,
  /^https:\/\/sonarsource\.github\.io\/helm-chart-sonarqube$/,
  /^oci:\/\/registry-1\.docker\.io\/.+$/,
];

/**
 * Git subset of the allowlist. Manifests/Kustomize sources must come from
 * a TomTom Git repository, even though the broader allowlist also accepts
 * Helm-HTTP / OCI hosts.
 */
const GIT_ALLOWED_PATTERNS: RegExp[] = [
  /^https:\/\/github\.com\/tomtom-internal\/[^/]+\/?$/,
  /^https:\/\/github\.com\/tomtom-international\/[^/]+\/?$/,
];

function urlMatchesPatterns(url: string, patterns: RegExp[]): boolean {
  return patterns.some(p => p.test(url));
}

export function containsTemplateExpression(value: string): boolean {
  return value.includes('{{') && value.includes('}}');
}

// Compile schemas once at module load. These validators are hot paths (called
// on every render of the corresponding sections) so re-instantiating Ajv per
// call would add noticeable overhead.
const sharedAjv = new Ajv();
const validateGeneratorsSchema = sharedAjv.compile(argoCDGeneratorSchema);
const validateImagesSchema = sharedAjv.compile(imagesSchema);
const validateTemplatePatchSchema = sharedAjv.compile(
  argoCDTemplatePatchSchema,
);

const defaultInputPattern = '^[a-zA-Z0-9-._/]+$';
const namespacePattern = '^[a-z0-9-]+$';
const gitRefPart = '[a-zA-Z0-9-._/]+';
const gitBranchPattern = `^${gitRefPart}$`;
const semverConstraintPart = '[<>=~^! ]*v?[0-9xX*][0-9a-zA-Z.*<>=~^!| -]*';
const targetRevisionPattern = `^(?:${gitRefPart}|${semverConstraintPart})$`;
const appsetTemplatePattern =
  '^[a-zA-Z0-9-._/:]*(?:{{[a-zA-Z0-9-._/:"| ]+}}[a-zA-Z0-9-._/:]*)*$';
const DNS_LABEL = '[a-z0-9]([-a-z0-9]*[a-z0-9])?';
const METADATA_KEY_PREFIX_PART = `${DNS_LABEL}(\\.${DNS_LABEL})*`;
const METADATA_SEGMENT = '[a-zA-Z0-9]([-._a-zA-Z0-9]*[a-zA-Z0-9])?';
const METADATA_KEY_PATTERN = `^(${METADATA_KEY_PREFIX_PART}\\/)?${METADATA_SEGMENT}$`;
const LABEL_VALUE_PATTERN = `^(${METADATA_SEGMENT})?$`;
const MAX_LABEL_VALUE_LENGTH = 63;

export const validateInputPattern = (
  input: string,
  pattern: string = defaultInputPattern,
): boolean => {
  if (!new RegExp(pattern).test(input.trim())) {
    return false;
  }
  if (input.startsWith('-') || input.endsWith('-')) {
    return false;
  }
  return true;
};

export const validateTargetRevisionPattern = (input: string): boolean => {
  return new RegExp(targetRevisionPattern).test(input.trim());
};

export const validateGitBranchPattern = (input: string): boolean => {
  return new RegExp(gitBranchPattern).test(input.trim());
};

export const validateAppSetTemplatePattern = (input: string): boolean => {
  return new RegExp(appsetTemplatePattern).test(input.trim());
};

export const isAllowedNamespaces = (
  clusterNamespaces: string[],
  namespace: string,
): boolean => {
  const namespacePrefix = clusterNamespaces.filter(ns => ns.endsWith('*'));
  const namespaces = clusterNamespaces.filter(ns => !ns.endsWith('*'));

  if (namespaces.includes(namespace)) {
    return true;
  }

  for (const prefix of namespacePrefix) {
    if (namespace.startsWith(prefix.slice(0, -1))) {
      return true;
    }
  }

  return false;
};

export const validateMetadataKey = (input: string): boolean => {
  return new RegExp(METADATA_KEY_PATTERN).test(input.trim());
};

export const validateLabelValue = (
  value: string,
  options: { enableAppSetSupport?: boolean } = {},
): boolean => {
  const { enableAppSetSupport = false } = options;
  if (enableAppSetSupport && containsTemplateExpression(value)) {
    return validateAppSetTemplatePattern(value);
  }
  return (
    value.trim().length <= MAX_LABEL_VALUE_LENGTH &&
    new RegExp(LABEL_VALUE_PATTERN).test(value.trim())
  );
};

export const validateMetadataKeyDuplicates = (
  metadata: MetadataKeyValuePair[],
): boolean => {
  const uniqueKeys = new Set<string>();
  for (const item of metadata) {
    const trimmedKey = item.key.trim();
    if (trimmedKey && uniqueKeys.has(trimmedKey)) {
      return false;
    }
    if (trimmedKey) {
      uniqueKeys.add(trimmedKey);
    }
  }
  return true;
};

export const findDuplicateMetadataKeys = (
  metadata: MetadataKeyValuePair[],
): string[] => {
  const keyCount = new Map<string, number>();
  const duplicates = new Set<string>();

  for (const item of metadata) {
    const trimmedKey = item.key.trim();
    if (trimmedKey) {
      const count = (keyCount.get(trimmedKey) || 0) + 1;
      keyCount.set(trimmedKey, count);
      if (count > 1) {
        duplicates.add(trimmedKey);
      }
    }
  }

  return Array.from(duplicates);
};

export const applicationNameFormatErrors = (name: string): string => {
  if (!name || !name.trim()) {
    return 'Application name is required';
  }
  if (!/^[a-z0-9-]+$/.test(name)) {
    return 'Application name must have only lowercase letters, numbers, and hyphens';
  }
  if (name.length > 46) {
    return 'Application name must be at most 46 characters';
  }
  return '';
};

export const applicationSetNameFormatErrors = (name: string): string => {
  if (!name || !name.trim()) {
    return 'ApplicationSet name is required';
  }
  if (!/^[a-z0-9-]+$/.test(name)) {
    return 'ApplicationSet name must have only lowercase letters, numbers, and hyphens';
  }
  if (name.length > 46) {
    return 'ApplicationSet name must be at most 46 characters';
  }
  return '';
};

export const projectInputErrors = (project: string): string => {
  if (!project || !project.trim()) {
    return 'Project is required';
  }
  return '';
};

export const clusterInputErrors = (
  cluster: ClusterConfiguration | null,
): string => {
  if (!cluster) {
    return 'Cluster is required';
  }
  if (!cluster.name) {
    return 'Cluster name is required';
  }
  return '';
};

export const namespaceInputErrors = (
  namespace: string,
  allowedNamespaces: string[],
  useAppSetTemplate?: boolean,
): string => {
  if (!namespace || !namespace.trim()) {
    return 'Namespace is required';
  }
  if (useAppSetTemplate) {
    if (!validateAppSetTemplatePattern(namespace)) {
      return 'Namespace must be a valid template expression';
    }
  } else {
    if (namespace.length < 2 || namespace.length > 63) {
      return 'Namespace must be between 2 and 63 characters long';
    }
    if (!validateInputPattern(namespace, namespacePattern)) {
      return 'Namespace must have only lowercase letters, numbers, and hyphens (2–63 characters)';
    }
    if (
      allowedNamespaces.length > 0 &&
      !isAllowedNamespaces(allowedNamespaces, namespace)
    ) {
      return `Namespace must match one of: ${allowedNamespaces.join(', ')}`;
    }
  }
  return '';
};

const HELM_RELEASE_NAME_PATTERN = /^[a-z0-9]([-a-z0-9]*[a-z0-9])?$/;
const MAX_HELM_RELEASE_NAME_LENGTH = 53;

/** URL must be in the allowlist. AppSet templated URLs are exempt when enabled. */
export function validateSourceRepoURL(
  url: string,
  options: { enableAppSetSupport?: boolean } = {},
): boolean {
  if (!url) return false;
  const { enableAppSetSupport = false } = options;
  if (enableAppSetSupport && containsTemplateExpression(url)) return true;
  return urlMatchesPatterns(url, ALLOWED_REPO_PATTERNS);
}

export function validateSourceGitURL(url: string): boolean {
  if (!url) return false;
  return urlMatchesPatterns(url, GIT_ALLOWED_PATTERNS);
}

export function validateSourceRevision(
  value: string,
  options: { enableAppSetSupport?: boolean } = {},
): boolean {
  if (!value) return false;
  const { enableAppSetSupport = false } = options;
  if (enableAppSetSupport && containsTemplateExpression(value)) {
    return validateAppSetTemplatePattern(value);
  }
  return validateTargetRevisionPattern(value);
}

export function validateSourceInputPattern(
  value: string,
  options: { enableAppSetSupport?: boolean } = {},
): boolean {
  if (!value) return false;
  const { enableAppSetSupport = false } = options;
  if (enableAppSetSupport && containsTemplateExpression(value)) {
    return validateAppSetTemplatePattern(value);
  }
  return validateInputPattern(value, '^[a-zA-Z0-9-._/*]+$');
}

export function validateHelmReleaseName(
  name: string,
  options: { enableAppSetSupport?: boolean } = {},
): boolean {
  if (!name) return false;
  const { enableAppSetSupport = false } = options;
  if (enableAppSetSupport && containsTemplateExpression(name)) {
    return validateAppSetTemplatePattern(name);
  }
  if (name.length > MAX_HELM_RELEASE_NAME_LENGTH) return false;
  return HELM_RELEASE_NAME_PATTERN.test(name);
}

export function sourceInputErrors(
  source: SourceConfiguration | null,
  options: { enableAppSetSupport?: boolean } = {},
): string {
  if (!source) return 'Source is required';
  if (!source.deploymentType) return 'Deployment type is required';
  if (!source.repoURL) return 'Repository URL is required';

  const isTemplated = containsTemplateExpression(source.repoURL);
  const inferred = inferRepoType(source.repoURL);
  const repoType = resolveRepoType(source);
  const { enableAppSetSupport = false } = options;

  if (!validateSourceRepoURL(source.repoURL, { enableAppSetSupport })) {
    return 'Repository URL is not in the allowlist';
  }

  if (!source.targetRevision) return 'Revision/version is required';

  const isHelmURL = inferred === 'helm' || inferred === 'oci';
  if (
    (source.deploymentType === 'manifests' ||
      source.deploymentType === 'kustomize') &&
    isHelmURL
  ) {
    return `${source.deploymentType} requires a git URL`;
  }

  if (
    (source.deploymentType === 'manifests' ||
      source.deploymentType === 'kustomize') &&
    !isTemplated
  ) {
    if (!validateSourceGitURL(source.repoURL)) {
      return `${source.deploymentType} requires a TomTom Git repository URL`;
    }
  }

  if (source.path && source.chart) {
    return 'Only one of path or chart may be set for a source';
  }

  const isHelmHTTP = source.deploymentType === 'helm' && repoType === 'helm';
  const isHelmOCI = source.deploymentType === 'helm' && repoType === 'oci';
  if (isHelmHTTP) {
    if (!source.chart) return 'Chart name is required';
  } else if (isHelmOCI) {
    // No path/chart input required — encoded in the URL.
  } else {
    if (!source.path) return 'Path is required';
  }

  if (source.deploymentType === 'helm') {
    if (!source.helm) return 'Helm configuration is required';
    if (!source.helm.releaseName) return 'Release name is required';
  }

  // --- Regex / format checks ---
  if (!validateSourceRevision(source.targetRevision, { enableAppSetSupport })) {
    return 'Revision must contain only letters, numbers, hyphens, dots, underscores, or slashes';
  }

  if (
    source.path &&
    !validateSourceInputPattern(source.path, { enableAppSetSupport })
  ) {
    return 'Path must have only letters, numbers, hyphens, dots, underscores or slashes';
  }

  if (
    source.chart &&
    !validateSourceInputPattern(source.chart, { enableAppSetSupport })
  ) {
    return 'Chart name must have only letters, numbers, hyphens, dots, underscores or slashes';
  }

  if (source.deploymentType === 'helm' && source.helm) {
    if (
      source.helm.releaseName &&
      !validateHelmReleaseName(source.helm.releaseName, { enableAppSetSupport })
    ) {
      return 'Release name must be lowercase alphanumeric with hyphens; start and end alphanumeric; max 53 chars';
    }

    if (source.helm.valueFiles) {
      for (const vf of source.helm.valueFiles) {
        if (!vf) continue;
        if (!validateSourceInputPattern(vf, { enableAppSetSupport })) {
          return 'Value file paths must have only letters, numbers, hyphens, dots, underscores or slashes';
        }
      }
    }

    if (source.helm.valuesSource) {
      const { repoURL: vsURL, targetRevision: vsRev } =
        source.helm.valuesSource;
      if (vsURL && !validateSourceGitURL(vsURL)) {
        return 'Values source must be a TomTom Git repository URL';
      }
      if (vsRev && !validateTargetRevisionPattern(vsRev)) {
        return 'Values source revision must contain only letters, numbers, hyphens, dots, underscores, or slashes';
      }
    }
  }

  return '';
}

export const metadataInputErrors = (
  metadata: KubernetesMetadata,
  options: { enableAppSetSupport?: boolean } = {},
): string => {
  const { enableAppSetSupport = false } = options;
  if (
    metadata?.annotations &&
    !validateMetadataKeyDuplicates(metadata.annotations)
  ) {
    const duplicateKeys = findDuplicateMetadataKeys(metadata.annotations);
    return `Duplicate annotation key(s) found: ${duplicateKeys.join(
      ', ',
    )}. Annotation keys must be unique.`;
  }

  if (metadata?.annotations) {
    for (const annotation of metadata.annotations) {
      if (annotation.key && !validateMetadataKey(annotation.key)) {
        return `Invalid annotation key '${annotation.key}'. Annotation keys must be valid Kubernetes annotations consisting of alphanumeric, dashes, dots, underscores with an optional DNS subdomain prefix.`;
      }
    }
  }

  if (metadata?.labels && !validateMetadataKeyDuplicates(metadata.labels)) {
    const duplicateKeys = findDuplicateMetadataKeys(metadata.labels);
    return `Duplicate label key(s) found: ${duplicateKeys.join(
      ', ',
    )}. Label keys must be unique.`;
  }

  if (metadata?.labels) {
    for (const label of metadata.labels) {
      if (label.key && !validateMetadataKey(label.key)) {
        return `Invalid label key '${label.key}'. Label keys must be valid Kubernetes labels consisting of alphanumeric, dashes, dots, underscores with an optional DNS subdomain prefix.`;
      }
      if (
        label.value &&
        !validateLabelValue(label.value, { enableAppSetSupport })
      ) {
        return `Invalid label value '${label.value}' for key '${label.key}'. Label values must be 63 characters or less and consist of alphanumerics, dashes, dots, or underscores, beginning and ending with an alphanumeric character.`;
      }
    }
  }
  return '';
};

export const syncOptionsInputErrors = (syncOptions: SyncOption[]): string => {
  const uniqueOptions = new Set<string>();
  for (const option of syncOptions) {
    if (uniqueOptions.has(option.key)) {
      return `Duplicate sync option key found: ${option.key}. Sync option keys must be unique.`;
    }
    uniqueOptions.add(option.key);
  }
  return '';
};

export const generatorsInputErrors = (generators: string): string => {
  if (!generators || generators.trim() === '') {
    return 'Generators input cannot be empty';
  }
  let parsed: any;
  try {
    parsed = YAML.parse(generators);
  } catch (error) {
    return 'Invalid YAML format';
  }
  if (!parsed || !Array.isArray(parsed.generators)) {
    return 'YAML must contain a top-level "generators" array';
  }
  if (!Array.isArray(parsed.generators) || parsed.generators.length === 0) {
    return 'Generators array must contain at least one generator';
  }
  const valid = validateGeneratorsSchema(parsed);
  if (!valid && validateGeneratorsSchema.errors) {
    return 'Schema validation error';
  }
  return '';
};

export interface GitWriteBackFieldErrors {
  repository?: string;
  branch?: string;
}

export const gitWriteBackFieldErrors = (
  config: GitWriteBackConfig | undefined,
): GitWriteBackFieldErrors => {
  const errors: GitWriteBackFieldErrors = {};
  if (!config) return errors;
  // The repository field is a strict project-repo dropdown in the UI, but a
  // pre-fill from an AppSet-templated repo would slip a {{ … }} URL in. There
  // is no shared URL pattern in this file, so check inline.
  if (config.repository && /\{\{|\}\}/.test(config.repository)) {
    errors.repository =
      'Write-back repository cannot contain template variables ({{ … }}); image updater requires a static URL';
  }
  if (config.branch && !validateGitBranchPattern(config.branch)) {
    errors.branch =
      'Revision must contain only letters, numbers, hyphens, dots, underscores, or slashes (no template variables)';
  }
  return errors;
};

export const gitWriteBackInputErrors = (
  config: GitWriteBackConfig | undefined,
): string => {
  const errs = gitWriteBackFieldErrors(config);
  return errs.repository ?? errs.branch ?? '';
};

export const imagesInputErrors = (imagesYAML: string): string => {
  if (!imagesYAML || imagesYAML.trim() === '') {
    return 'Images YAML is required';
  }
  let parsed: any;
  try {
    parsed = YAML.parse(imagesYAML);
  } catch (error) {
    return 'Invalid YAML format';
  }
  if (!parsed || !Array.isArray(parsed.images)) {
    return 'YAML must contain a top-level "images" array';
  }
  if (parsed.images.length === 0) {
    return 'Images array must contain at least one entry';
  }
  if (!validateImagesSchema(parsed)) {
    return 'Schema validation error';
  }
  return '';
};

export const templatePatchInputErrors = (templatePatch: string): string => {
  if (!templatePatch || templatePatch.trim() === '') {
    return '';
  }
  try {
    YAML.parse(templatePatch);
  } catch (error) {
    return 'Invalid YAML format';
  }
  const valid = validateTemplatePatchSchema(templatePatch);
  if (!valid && validateTemplatePatchSchema.errors) {
    return 'Must start with "templatePatch: |" (no leading/trailing spaces), followed by lines indented with at least 2 spaces.';
  }
  return '';
};

const applicationNameInputErrors = async (
  applicationName: string,
  projectId: string,
  catalogApi?: CatalogApi,
  fillEditFormData?: boolean,
): Promise<string> => {
  const formatError = applicationNameFormatErrors(applicationName);
  if (formatError) {
    return formatError;
  }

  if (catalogApi && !fillEditFormData) {
    try {
      const { items } = await catalogApi.getEntities({
        filter: {
          kind: 'Component',
          'spec.type': 'daas-application',
          'spec.projectId': projectId,
          'spec.applicationName': applicationName,
        },
        fields: ['spec.applicationName'],
      });
      if (items.length > 0) {
        return `Application "${applicationName}" already exists in project "${projectId}"`;
      }
    } catch {
      // Skip uniqueness check if catalog is unreachable to avoid blocking users
    }
  }

  return '';
};

const applicationSetNameInputErrors = async (
  applicationSetName: string,
  projectId: string,
  catalogApi?: CatalogApi,
  fillEditFormData?: boolean,
): Promise<string> => {
  const formatError = applicationSetNameFormatErrors(applicationSetName);
  if (formatError) {
    return formatError;
  }

  if (catalogApi && !fillEditFormData) {
    try {
      const { items } = await catalogApi.getEntities({
        filter: {
          kind: 'Component',
          'spec.type': 'daas-application-set',
          'spec.projectId': projectId,
          'spec.applicationSetName': applicationSetName,
        },
        fields: ['spec.applicationSetName'],
      });
      if (items.length > 0) {
        return `ApplicationSet "${applicationSetName}" already exists in project "${projectId}"`;
      }
    } catch {
      // Skip uniqueness check if catalog is unreachable to avoid blocking users
    }
  }

  return '';
};

export const daasApplicationInputValidation = async (
  value: DaasApplicationInputData,
  validation: FieldValidation,
  context: {
    apiHolder: ApiHolder;
    uiSchema?: FieldExtensionUiSchema<string, DaasApplicationInputUiOptions>;
  },
): Promise<void> => {
  if (!value) {
    validation.addError('Application data is required');
    return;
  }

  const projectError = projectInputErrors(value.project);
  if (projectError) {
    validation.addError(projectError);
    return;
  }

  const catalogApi = context?.apiHolder?.get(catalogApiRef);
  const fillEditFormData = context?.uiSchema?.['ui:options']
    ?.fillEditFormData as boolean | undefined;
  const applicationNameError = await applicationNameInputErrors(
    value.applicationName,
    value.project,
    catalogApi,
    fillEditFormData,
  );
  if (applicationNameError) {
    validation.addError(applicationNameError);
    return;
  }

  const clusterError = clusterInputErrors(value.cluster);
  if (clusterError) {
    validation.addError(clusterError);
    return;
  }

  const namespaceError = namespaceInputErrors(
    value.cluster?.namespace || '',
    value.cluster?.allowedNamespaces || [],
  );
  if (namespaceError) {
    validation.addError(namespaceError);
    return;
  }

  const sourceError = sourceInputErrors(value.source);
  if (sourceError) {
    validation.addError(sourceError);
    return;
  }

  const syncOptionsError = syncOptionsInputErrors(
    value.syncPolicy?.syncOptions || [],
  );
  if (syncOptionsError) {
    validation.addError(syncOptionsError);
    return;
  }

  const namespaceMetadataError = metadataInputErrors(
    value.syncPolicy?.autoCreateNamespace?.managedNamespaceMetadata,
  );
  if (namespaceMetadataError) {
    validation.addError(namespaceMetadataError);
    return;
  }

  const metadataError = metadataInputErrors(value.applicationMetadata);
  if (metadataError) {
    validation.addError(metadataError);
    return;
  }

  if (value.imageUpdater?.enabled) {
    const writeBackError = gitWriteBackInputErrors(
      value.imageUpdater.gitWriteBackConfig,
    );
    if (writeBackError) {
      validation.addError(writeBackError);
      return;
    }
    const imagesError = imagesInputErrors(value.imageUpdater.imagesYAML ?? '');
    if (imagesError) {
      validation.addError(imagesError);
      return;
    }
  }
};

export const daasApplicationSetInputValidation = async (
  value: DaasApplicationSetInputData,
  validation: FieldValidation,
  context: {
    apiHolder: ApiHolder;
    uiSchema?: FieldExtensionUiSchema<string, DaasApplicationSetInputUiOptions>;
  },
): Promise<void> => {
  if (!value) {
    validation.addError('ApplicationSet data is required');
    return;
  }

  const projectError = projectInputErrors(value.project);
  if (projectError) {
    validation.addError(projectError);
    return;
  }

  const catalogApi = context?.apiHolder?.get(catalogApiRef);
  const fillEditFormData = context?.uiSchema?.['ui:options']
    ?.fillEditFormData as boolean | undefined;
  const applicationSetNameError = await applicationSetNameInputErrors(
    value.applicationSetName,
    value.project,
    catalogApi,
    fillEditFormData,
  );
  if (applicationSetNameError) {
    validation.addError(applicationSetNameError);
    return;
  }

  const generatorsError = generatorsInputErrors(value.generators);
  if (generatorsError) {
    validation.addError(generatorsError);
    return;
  }

  if (!value.appNameTemplate || !value.appNameTemplate.trim()) {
    validation.addError('Application name template is required');
    return;
  }

  const clusterError = clusterInputErrors(value.cluster);
  if (clusterError) {
    validation.addError(clusterError);
    return;
  }

  const namespaceError = namespaceInputErrors(
    value.cluster?.namespace || '',
    value.cluster?.allowedNamespaces || [],
    true,
  );
  if (namespaceError) {
    validation.addError(namespaceError);
    return;
  }

  const sourceError = sourceInputErrors(value.source, {
    enableAppSetSupport: true,
  });
  if (sourceError) {
    validation.addError(sourceError);
    return;
  }

  const syncOptionsError = syncOptionsInputErrors(
    value.syncPolicy?.syncOptions || [],
  );
  if (syncOptionsError) {
    validation.addError(syncOptionsError);
    return;
  }

  const namespaceMetadataError = metadataInputErrors(
    value.syncPolicy?.autoCreateNamespace?.managedNamespaceMetadata,
    { enableAppSetSupport: true },
  );
  if (namespaceMetadataError) {
    validation.addError(namespaceMetadataError);
    return;
  }

  const metadataError = metadataInputErrors(value.applicationMetadata, {
    enableAppSetSupport: true,
  });
  if (metadataError) {
    validation.addError(metadataError);
    return;
  }

  const templatePatchError = templatePatchInputErrors(
    value.templatePatch || '',
  );
  if (templatePatchError) {
    validation.addError(templatePatchError);
    return;
  }

  if (value.imageUpdater?.enabled) {
    const writeBackError = gitWriteBackInputErrors(
      value.imageUpdater.gitWriteBackConfig,
    );
    if (writeBackError) {
      validation.addError(writeBackError);
      return;
    }
    const imagesError = imagesInputErrors(value.imageUpdater.imagesYAML ?? '');
    if (imagesError) {
      validation.addError(imagesError);
      return;
    }
  }
};
